import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import {
    AlignCenter, AlignLeft, AlignRight, ArrowDown, ArrowLeft, ArrowUp, Bold, Copy, Footprints, Heading1, Image, Italic, Link2, Minus,
    Monitor, MousePointerClick, MoveVertical, Pause, Plus, Send, Smartphone, Sparkles, Ticket, Trash2, Type, Users,
} from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import Loader from '../components/Loader';
import { Badge, Confirm, Toast } from '../components/userProject/UpUi';
import { projectApi } from '../components/userProject/api';
import { CAMPAIGN_STATUS } from './Emails';

const BLOCKS = {
    logo: { label: 'Logo', icon: Sparkles, make: () => ({ type: 'logo' }) },
    title: { label: 'Titre', icon: Heading1, make: () => ({ type: 'title', text: 'Ton titre', size: 28, align: 'left' }) },
    text: { label: 'Texte', icon: Type, make: () => ({ type: 'text', text: 'Ton texte…', size: 16, align: 'left' }) },
    button: { label: 'Bouton', icon: MousePointerClick, make: () => ({ type: 'button', label: 'Créer mon compte', url: '{{lien}}', align: 'left' }) },
    code: { label: 'Code d’invitation', icon: Ticket, make: () => ({ type: 'code' }) },
    image: { label: 'Image', icon: Image, make: () => ({ type: 'image', url: '', alt: '' }) },
    divider: { label: 'Séparateur', icon: Minus, make: () => ({ type: 'divider' }) },
    spacer: { label: 'Espace', icon: MoveVertical, make: () => ({ type: 'spacer', size: 16 }) },
    footer: { label: 'Pied de page', icon: Footprints, make: () => ({ type: 'footer', text: 'Tu reçois cet email parce que tu es une personne de confiance.' }) },
}
const VARS = [
    ['{{code}}', 'code d’invitation'],
    ['{{lien}}', 'lien d’inscription (code rempli)'],
    ['{{nom}}', 'nom'],
    ['{{email}}', 'email'],
    ['{{site}}', 'adresse du site'],
]
const THEME = [['bg', 'Fond'], ['card', 'Carte'], ['text', 'Texte'], ['muted', 'Texte discret'], ['accent', 'Couleur principale']]
const key = () => Math.random().toString(36).slice(2, 9)
const withKeys = (design) => ({ ...design, blocks: (design?.blocks || []).map((b) => ({ ...b, _k: b._k || key() })) })
const summary = (b) => {
    if (b.type === 'title' || b.type === 'text' || b.type === 'footer') return (b.text || '').replace(/\s+/g, ' ').slice(0, 60)
    if (b.type === 'button') return `${b.label || ''} → ${b.url || ''}`
    if (b.type === 'image') return b.url || 'pas d’image'
    if (b.type === 'spacer') return `${b.size || 16} px`
    return ''
}

/** Zone de texte avec gras, italique, lien et variables (insérés à la position du curseur). */
const RichText = ({ value, onChange, rows = 7 }) => {
    const ref = useRef()
    const wrap = (before, after = before, placeholder = 'texte') => {
        const el = ref.current
        const { selectionStart: a, selectionEnd: b } = el
        const sel = value.slice(a, b) || placeholder
        const next = value.slice(0, a) + before + sel + after + value.slice(b)
        onChange(next)
        requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + before.length, a + before.length + sel.length) })
    }
    const link = () => {
        const el = ref.current
        const { selectionStart: a, selectionEnd: b } = el
        const sel = value.slice(a, b) || 'texte du lien'
        const ins = `[${sel}](https://)`
        onChange(value.slice(0, a) + ins + value.slice(b))
        requestAnimationFrame(() => { el.focus(); const p = a + sel.length + 3; el.setSelectionRange(p, p + 8) })
    }
    const insert = (v) => {
        const el = ref.current
        const a = el.selectionStart
        onChange(value.slice(0, a) + v + value.slice(el.selectionEnd))
        requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + v.length, a + v.length) })
    }
    return (
        <div className='eme-rich'>
            <div className='eme-rich-bar'>
                <button type='button' onClick={() => wrap('**')} title='Gras'><Bold size={15} /></button>
                <button type='button' onClick={() => wrap('*')} title='Italique'><Italic size={15} /></button>
                <button type='button' onClick={link} title='Lien'><Link2 size={15} /></button>
                <span className='eme-rich-sep' />
                {VARS.slice(0, 3).map(([v, label]) => <button key={v} type='button' onClick={() => insert(v)} title={label}>{v}</button>)}
            </div>
            <textarea ref={ref} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
            <small className='up-muted'>**gras**, *italique*, [texte](https://…), une ligne vide = nouveau paragraphe</small>
        </div>
    )
}

const AlignPick = ({ value, onChange }) => (
    <div className='eme-seg'>
        {[['left', AlignLeft], ['center', AlignCenter], ['right', AlignRight]].map(([a, Icon]) => (
            <button key={a} type='button' className={value === a ? 'on' : ''} onClick={() => onChange(a)} title={a}><Icon size={15} /></button>
        ))}
    </div>
)

const Field = ({ label, children, hint }) => (
    <label className='eme-field'>
        <span>{label}</span>
        {children}
        {hint && <small className='up-muted'>{hint}</small>}
    </label>
)

/** Réglages du bloc choisi. */
const BlockSettings = ({ block, onChange }) => {
    const set = (k, v) => onChange({ ...block, [k]: v })
    switch (block.type) {
        case 'title':
            return (
                <>
                    <Field label='Titre'><input value={block.text || ''} onChange={(e) => set('text', e.target.value)} /></Field>
                    <Field label={`Taille : ${block.size || 28} px`}><input type='range' min={18} max={44} value={block.size || 28} onChange={(e) => set('size', Number(e.target.value))} /></Field>
                    <Field label='Alignement'><AlignPick value={block.align} onChange={(v) => set('align', v)} /></Field>
                </>
            )
        case 'text':
        case 'footer':
            return (
                <>
                    <Field label={block.type === 'footer' ? 'Pied de page' : 'Texte'}><RichText value={block.text || ''} onChange={(v) => set('text', v)} rows={block.type === 'footer' ? 3 : 9} /></Field>
                    {block.type === 'text' && (
                        <>
                            <Field label={`Taille : ${block.size || 16} px`}><input type='range' min={12} max={22} value={block.size || 16} onChange={(e) => set('size', Number(e.target.value))} /></Field>
                            <Field label='Alignement'><AlignPick value={block.align} onChange={(v) => set('align', v)} /></Field>
                            <label className='eme-check'><input type='checkbox' checked={!!block.muted} onChange={(e) => set('muted', e.target.checked)} /> Texte discret (gris)</label>
                        </>
                    )}
                </>
            )
        case 'button':
            return (
                <>
                    <Field label='Texte du bouton'><input value={block.label || ''} onChange={(e) => set('label', e.target.value)} /></Field>
                    <Field label='Lien' hint='{{lien}} = page d’inscription avec son code déjà rempli'><input value={block.url || ''} onChange={(e) => set('url', e.target.value)} placeholder='{{lien}} ou https://…' /></Field>
                    <Field label='Couleur (vide = couleur principale)'>
                        <div className='eme-color'><input type='color' value={block.color || '#5145ff'} onChange={(e) => set('color', e.target.value)} />{block.color && <button type='button' className='up-linkbtn' onClick={() => set('color', '')}>par défaut</button>}</div>
                    </Field>
                    <Field label='Alignement'><AlignPick value={block.align} onChange={(v) => set('align', v)} /></Field>
                </>
            )
        case 'image':
            return (
                <>
                    <Field label='Adresse de l’image (https://…)' hint='Image hébergée en ligne (jpg / png), 1 200 px de large idéalement'><input value={block.url || ''} onChange={(e) => set('url', e.target.value)} /></Field>
                    <Field label='Texte alternatif'><input value={block.alt || ''} onChange={(e) => set('alt', e.target.value)} /></Field>
                    <Field label='Lien au clic (facultatif)'><input value={block.link || ''} onChange={(e) => set('link', e.target.value)} placeholder='{{lien}} ou https://…' /></Field>
                </>
            )
        case 'spacer':
            return <Field label={`Hauteur : ${block.size || 16} px`}><input type='range' min={4} max={80} value={block.size || 16} onChange={(e) => set('size', Number(e.target.value))} /></Field>
        case 'code':
            return <p className='up-muted'>Le code d’invitation du destinataire, en grand. Coche « code d’invitation unique » dans l’onglet Envoi.</p>
        case 'logo':
            return <p className='up-muted'>Le logo PrimeStation (texte, s’affiche dans toutes les messageries).</p>
        default:
            return <p className='up-muted'>Rien à régler.</p>
    }
}

/**
 * Éditeur d'une campagne (grande page) : blocs à gauche, réglages au centre (bloc, email, envoi), aperçu exact à droite
 * (rendu par PrimeStation, comme l'email envoyé). Enregistrement automatique. Envoi : préparer les destinataires, email
 * de test, envoyer N emails (par lots), pause ; progression relue toutes les 2 s pendant l'envoi.
 */
const EmailEditor = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { setCurrentPage } = useContext(AppContext)
    const pApi = useMemo(() => projectApi('primestation'), [])
    const [camp, setCamp] = useState(null)
    const [draft, setDraft] = useState(null)
    const [dirty, setDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [sel, setSel] = useState(0)
    const [tab, setTab] = useState('bloc')
    const [view, setView] = useState('desktop')
    const [preview, setPreview] = useState(null)
    const [audiences, setAudiences] = useState({})
    const [simulation, setSimulation] = useState(true)
    const [mode, setMode] = useState(null) // { links, blocked } : liens vers localhost → vrai envoi refusé
    const [testTo, setTestTo] = useState('')
    const [limit, setLimit] = useState(2000)
    const [ask, setAsk] = useState(null)
    const [busy, setBusy] = useState(null)
    const [error, setError] = useState('')
    const [toast, setToast] = useState(null)
    const notify = useCallback((text, isError = false) => setToast({ text, error: isError, at: Date.now() }), [])

    // emails tapés à la main (audience « custom ») : même règle que PrimeStation (services/campaigns.js parseEmails)
    const typedEmails = [...new Set(String(draft?.emails || '').split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)))]
    useEffect(() => { setCurrentPage('emails') }, [setCurrentPage])
    const load = useCallback(async () => {
        try {
            const c = await pApi.get(`/emails/${id}`)
            setCamp(c)
            setDraft((d) => d || { name: c.name, subject: c.subject, preheader: c.preheader || '', design: withKeys(c.design), audience: c.audience, withCode: c.withCode, emails: c.emails || '' })
            setError('')
            return c
        } catch (e) { setError(e.message) }
    }, [pApi, id])
    useEffect(() => { load() }, [load])
    useEffect(() => {
        pApi.get('/emails').then((r) => { setAudiences(r.audiences || {}); setSimulation(r.simulation); setMode(r.mode || null) }, () => {})
    }, [pApi])

    const locked = camp?.running || camp?.status === 'sending'
    const change = (patch) => { if (locked) return; setDraft((d) => ({ ...d, ...patch })); setDirty(true) }
    const setBlocks = (fn) => change({ design: { ...draft.design, blocks: fn(draft.design.blocks) } })

    // enregistrement automatique (1,2 s après la dernière modification)
    useEffect(() => {
        if (!dirty || !draft) return undefined
        const t = setTimeout(async () => {
            setSaving(true)
            try {
                const c = await pApi.put(`/emails/${id}`, { ...draft, design: { ...draft.design, blocks: draft.design.blocks.map(({ _k, ...b }) => b) } })
                setCamp((old) => ({ ...old, ...c }))
                setDirty(false)
            } catch (e) { notify(e.message, true) }
            setSaving(false)
        }, 1200)
        return () => clearTimeout(t)
    }, [dirty, draft, pApi, id, notify])

    // aperçu exact (rendu par PrimeStation), 0,35 s après la dernière modification
    useEffect(() => {
        if (!draft) return undefined
        const t = setTimeout(async () => {
            try {
                setPreview(await pApi.post('/emails/preview', { subject: draft.subject, preheader: draft.preheader, design: { ...draft.design, blocks: draft.design.blocks.map(({ _k, ...b }) => b) } }))
            } catch (e) { /* aperçu suivant */ }
        }, 350)
        return () => clearTimeout(t)
    }, [draft, pApi])

    // pendant l'envoi : progression relue toutes les 2 s
    useEffect(() => {
        if (!locked) return undefined
        const t = setInterval(() => { load() }, 2000)
        return () => clearInterval(t)
    }, [locked, load])

    if (error) return <MainLayout><CenterLayout><div className='up-page'><p className='up-error'>{error}</p></div></CenterLayout></MainLayout>
    if (!camp || !draft) return <MainLayout><CenterLayout><div className='up-loading'><Loader /></div></CenterLayout></MainLayout>

    const blocks = draft.design.blocks
    const block = blocks[sel]
    const add = (type) => {
        const b = { ...BLOCKS[type].make(), _k: key() }
        const at = Math.min(blocks.length, sel + 1)
        setBlocks((l) => [...l.slice(0, at), b, ...l.slice(at)])
        setSel(at)
        setTab('bloc')
    }
    const move = (i, d) => {
        const j = i + d
        if (j < 0 || j >= blocks.length) return
        setBlocks((l) => { const n = [...l]; [n[i], n[j]] = [n[j], n[i]]; return n })
        setSel(j)
    }
    const dup = (i) => { setBlocks((l) => [...l.slice(0, i + 1), { ...l[i], _k: key() }, ...l.slice(i + 1)]); setSel(i + 1) }
    const remove = (i) => { setBlocks((l) => l.filter((_, k) => k !== i)); setSel(Math.max(0, i - 1)) }

    const act = async (name, fn) => {
        setBusy(name)
        try { await fn() } catch (e) { notify(e.message, true) }
        setBusy(null)
    }
    const prepare = () => act('prepare', async () => {
        const r = await pApi.post(`/emails/${id}/prepare`)
        notify(`${r.queued.toLocaleString('fr-FR')} destinataires prêts (sans désinscrits ni déjà envoyés)`)
        await load()
    })
    const sendTest = () => act('test', async () => {
        const r = await pApi.post(`/emails/${id}/test`, { to: testTo })
        notify(r.simulated ? 'Simulation : pas de clé Resend ici, rien n’est parti' : `Email de test envoyé à ${testTo}`)
    })
    const launch = () => act('send', async () => {
        setAsk(null)
        const r = await pApi.post(`/emails/${id}/send`, { limit: Number(limit) || 0 })
        notify(r.simulated ? 'Envoi lancé en SIMULATION (rien ne part)' : 'Envoi lancé')
        await load()
    })
    const pause = () => act('pause', async () => { await pApi.post(`/emails/${id}/pause`); notify('Pause demandée : le lot en cours se termine') })

    const st = CAMPAIGN_STATUS[camp.status] || {}
    const queued = camp.counts?.queued || 0
    const pct = camp.total ? Math.round(((camp.sent + camp.failed) / camp.total) * 100) : 0

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page eme'>
                    <header className='eme-head'>
                        <button className='pay-act-btn' onClick={() => navigate('/emails')} title='Campagnes'><ArrowLeft size={18} color='#fff' /></button>
                        <input className='eme-name' value={draft.name} onChange={(e) => change({ name: e.target.value })} disabled={locked} />
                        <Badge tone={st.tone}>{st.label}</Badge>
                        {camp.simulated && <Badge>Simulation</Badge>}
                        <span className='up-muted eme-save'>{saving ? 'Enregistrement…' : dirty ? 'Modifications…' : 'Enregistré'}</span>
                    </header>

                    <div className='eme-grid'>
                        {/* ─── blocs ─── */}
                        <section className='eme-col eme-blocks'>
                            <p className='eme-col-title'>Ajouter un bloc</p>
                            <div className='eme-palette'>
                                {Object.entries(BLOCKS).map(([type, { label, icon: Icon }]) => (
                                    <button key={type} type='button' onClick={() => add(type)} disabled={locked}><Icon size={16} /> {label}</button>
                                ))}
                            </div>
                            <p className='eme-col-title'>L’email <span>· {blocks.length} blocs</span></p>
                            <ol className='eme-tree'>
                                {blocks.map((b, i) => {
                                    const Icon = BLOCKS[b.type]?.icon || Plus
                                    return (
                                        <li key={b._k} className={i === sel ? 'on' : ''} onClick={() => { setSel(i); setTab('bloc') }}>
                                            <Icon size={15} />
                                            <span><b>{BLOCKS[b.type]?.label || b.type}</b><small>{summary(b)}</small></span>
                                            {i === sel && !locked && (
                                                <span className='eme-tree-actions' onClick={(e) => e.stopPropagation()}>
                                                    <button type='button' onClick={() => move(i, -1)} title='Monter'><ArrowUp size={14} /></button>
                                                    <button type='button' onClick={() => move(i, 1)} title='Descendre'><ArrowDown size={14} /></button>
                                                    <button type='button' onClick={() => dup(i)} title='Dupliquer'><Copy size={14} /></button>
                                                    <button type='button' onClick={() => remove(i)} title='Supprimer'><Trash2 size={14} /></button>
                                                </span>
                                            )}
                                        </li>
                                    )
                                })}
                            </ol>
                        </section>

                        {/* ─── réglages ─── */}
                        <section className='eme-col eme-settings'>
                            <nav className='eme-tabs'>
                                {[['bloc', 'Bloc'], ['email', 'Email'], ['envoi', 'Envoi']].map(([k, label]) => (
                                    <button key={k} type='button' className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{label}</button>
                                ))}
                            </nav>
                            {tab === 'bloc' && (block
                                ? <fieldset disabled={locked}><p className='eme-col-title'>{BLOCKS[block.type]?.label}</p><BlockSettings block={block} onChange={(b) => setBlocks((l) => l.map((x, i) => (i === sel ? b : x)))} /></fieldset>
                                : <p className='up-muted'>Ajoute un bloc à gauche.</p>)}
                            {tab === 'email' && (
                                <fieldset disabled={locked}>
                                    <Field label='Objet' hint='Ce qu’on lit dans la boîte mail'><input value={draft.subject} maxLength={200} onChange={(e) => change({ subject: e.target.value })} /></Field>
                                    <Field label='Texte d’aperçu' hint='La ligne grise après l’objet dans Gmail / Outlook'><input value={draft.preheader} maxLength={200} onChange={(e) => change({ preheader: e.target.value })} /></Field>
                                    <p className='eme-col-title'>Couleurs</p>
                                    <div className='eme-theme'>
                                        {THEME.map(([k, label]) => (
                                            <label key={k}><input type='color' value={draft.design.theme?.[k] || '#000000'} onChange={(e) => change({ design: { ...draft.design, theme: { ...draft.design.theme, [k]: e.target.value } } })} /> {label}</label>
                                        ))}
                                    </div>
                                    <p className='eme-col-title'>Variables</p>
                                    <ul className='eme-vars'>{VARS.map(([v, label]) => <li key={v}><code>{v}</code> {label}</li>)}<li><code>{'{{desinscription}}'}</code> lien de désinscription (ajouté en bas de toute façon)</li></ul>
                                </fieldset>
                            )}
                            {tab === 'envoi' && (
                                <div className='eme-send'>
                                    {simulation && <p className='em-sim'>Simulation : ce PrimeStation n’a pas de clé Resend, rien ne peut partir.</p>}
                                    {!simulation && mode?.blocked && <p className='em-sim'>Envoi réel bloqué ici : les liens mèneraient à {mode.links}. Envoie depuis le PrimeStation en ligne.</p>}
                                    <fieldset disabled={locked}>
                                        <Field label='Destinataires'>
                                            <select value={draft.audience} onChange={(e) => change({ audience: e.target.value })}>
                                                {Object.entries(audiences).map(([k, a]) => <option key={k} value={k}>{a.label} ({(k === 'custom' ? typedEmails.length : a.count).toLocaleString('fr-FR')})</option>)}
                                            </select>
                                        </Field>
                                        {/* emails à la main : n'importe qui, même pas dans la base (un par ligne, ou séparés par des virgules) */}
                                        {draft.audience === 'custom' && (
                                            <Field label={`Emails (${typedEmails.length} valide${typedEmails.length > 1 ? 's' : ''})`}>
                                                <textarea
                                                    className='eme-emails'
                                                    rows={5}
                                                    value={draft.emails || ''}
                                                    onChange={(e) => change({ emails: e.target.value })}
                                                    placeholder={'client1@gmail.com\nclient2@hotmail.fr'}
                                                />
                                            </Field>
                                        )}
                                        <label className='eme-check'><input type='checkbox' checked={draft.withCode} onChange={(e) => change({ withCode: e.target.checked })} /> Un code d’invitation unique par personne ({'{{code}}'}, {'{{lien}}'})</label>
                                    </fieldset>
                                    <div className='eme-box'>
                                        <p className='eme-col-title'>Email de test</p>
                                        <div className='eme-row'>
                                            <input type='email' value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder='ton@email.fr' />
                                            <button className='btn cw' onClick={sendTest} disabled={!testTo || !!busy || dirty}><Send size={14} /> {busy === 'test' ? '…' : 'Tester'}</button>
                                        </div>
                                        <small className='up-muted'>Seulement à cette adresse, avec un code d’exemple.</small>
                                    </div>
                                    <div className='eme-box'>
                                        <p className='eme-col-title'>Envoi</p>
                                        <div className='eme-stats'>
                                            <span><b>{camp.total.toLocaleString('fr-FR')}</b> destinataires</span>
                                            <span><b>{camp.sent.toLocaleString('fr-FR')}</b> {camp.simulated ? 'simulés' : 'envoyés'}</span>
                                            <span><b>{queued.toLocaleString('fr-FR')}</b> à envoyer</span>
                                            {camp.failed > 0 && <span className='ko'><b>{camp.failed}</b> en échec</span>}
                                        </div>
                                        {camp.total > 0 && <div className='em-progress'><i style={{ width: `${pct}%` }} /></div>}
                                        {!locked && (
                                            <button className='btn cw' onClick={prepare} disabled={!!busy || dirty}><Users size={14} /> {busy === 'prepare' ? 'Préparation…' : camp.status === 'draft' ? 'Préparer les destinataires' : 'Repréparer (nouveaux inscrits)'}</button>
                                        )}
                                        {!locked && queued > 0 && (
                                            <div className='eme-row'>
                                                <span>Envoyer les</span>
                                                <input type='number' min={1} max={50000} value={limit} onChange={(e) => setLimit(e.target.value)} />
                                                <span>prochains</span>
                                                <button className='btn cs' onClick={() => setAsk('send')} disabled={!!busy || dirty || !(Number(limit) > 0)}><Send size={14} /> Envoyer</button>
                                            </div>
                                        )}
                                        {locked && <button className='btn up-danger-soft' onClick={pause} disabled={busy === 'pause'}><Pause size={14} /> Pause</button>}
                                        <small className='up-muted'>Conseil : 2 000 par jour au début, pour que Gmail ne classe pas en spam. Une personne ne reçoit jamais deux fois la même campagne.</small>
                                        {camp.startedAt && <small className='up-muted'>Commencée {moment(camp.startedAt).fromNow()}{camp.finishedAt ? ` · terminée ${moment(camp.finishedAt).fromNow()}` : ''}</small>}
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* ─── aperçu ─── */}
                        <section className='eme-col eme-preview'>
                            <div className='eme-preview-bar'>
                                <div className='eme-inbox'>
                                    <b>{preview?.subject || draft.subject}</b>
                                    <span>{draft.preheader}</span>
                                </div>
                                <div className='eme-seg'>
                                    <button type='button' className={view === 'desktop' ? 'on' : ''} onClick={() => setView('desktop')} title='Ordinateur'><Monitor size={15} /></button>
                                    <button type='button' className={view === 'mobile' ? 'on' : ''} onClick={() => setView('mobile')} title='Mobile'><Smartphone size={15} /></button>
                                </div>
                            </div>
                            <div className={`eme-frame ${view}`}>
                                {preview ? <iframe title='Aperçu de l’email' srcDoc={preview.html} sandbox='' /> : <div className='up-loading'><Loader /></div>}
                            </div>
                        </section>
                    </div>
                </div>
                {ask === 'send' && (
                    <Confirm
                        title={simulation ? 'Lancer l’envoi (simulation)' : 'Lancer l’envoi'}
                        text={simulation
                            ? `Simulation : ${Math.min(Number(limit), queued).toLocaleString('fr-FR')} emails passent par toutes les étapes, aucun ne part.`
                            : `${Math.min(Number(limit), queued).toLocaleString('fr-FR')} emails vont partir maintenant (« ${draft.subject} »)${draft.withCode ? ', chacun avec un code d’invitation unique' : ''}. Tape ENVOYER pour confirmer.`}
                        action='Envoyer'
                        danger={!simulation}
                        confirmText={simulation ? null : 'ENVOYER'}
                        onConfirm={launch}
                        onClose={() => setAsk(null)}
                    />
                )}
                <Toast toast={toast} onDone={() => setToast(null)} />
            </CenterLayout>
        </MainLayout>
    );
};

export default EmailEditor;
